// Motion helpers for the Settings page. All of them respect reduced motion.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

export const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)'

export function useReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches),
  )
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)')
    if (!mq) return undefined
    const onChange = e => setReduced(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return reduced
}

// Fades `ref` in with a short slide each time `key` changes. The first render is left alone.
export function useSwapIn(ref, key, { y = 10, scale = 1, duration = 320 } = {}) {
  const reduced = useReducedMotion()
  const lastKey = useRef(key)
  useLayoutEffect(() => {
    if (lastKey.current === key) return
    lastKey.current = key
    if (reduced || !ref.current?.animate) return
    ref.current.animate(
      [
        { opacity: 0, transform: `translateY(${y}px) scale(${scale})` },
        { opacity: 1, transform: 'translateY(0) scale(1)' },
      ],
      { duration, easing: EASE },
    )
  }, [ref, key, y, scale, duration, reduced])
}

// Position of the child marked data-key={activeKey}, for sliding highlights.
// `animate` stays false on the first measurement so the highlight doesn't fly in.
export function useIndicator(containerRef, activeKey) {
  const [box, setBox] = useState(null)

  const measure = useCallback(() => {
    const container = containerRef.current
    const el = container?.querySelector(`[data-key="${activeKey}"]`)
    if (!el || !container.offsetWidth) return
    setBox(prev => ({
      x: el.offsetLeft,
      y: el.offsetTop,
      w: el.offsetWidth,
      h: el.offsetHeight,
      animate: Boolean(prev),
    }))
  }, [containerRef, activeKey])

  useLayoutEffect(() => { measure() }, [measure])

  useEffect(() => {
    const container = containerRef.current
    if (!container || typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(() => measure())
    observer.observe(container)
    return () => observer.disconnect()
  }, [containerRef, measure])

  return box
}

export function indicatorStyle(box, axis, reduced) {
  if (!box) return { opacity: 0 }
  const move = axis === 'x' ? `translateX(${box.x}px)` : `translateY(${box.y}px)`
  return {
    transform: move,
    width: axis === 'x' ? box.w : undefined,
    height: axis === 'y' ? box.h : undefined,
    transition: box.animate && !reduced
      ? `transform 360ms ${EASE}, width 360ms ${EASE}, height 360ms ${EASE}`
      : 'none',
  }
}

// Keeps something mounted long enough to animate out. `shown` flips a frame
// after mounting so the enter transition has a starting point.
export function usePresence(open, duration = 200) {
  const [mounted, setMounted] = useState(open)
  const [shown, setShown] = useState(false)

  useEffect(() => {
    if (open) {
      setMounted(true)
      let inner = 0
      const outer = requestAnimationFrame(() => { inner = requestAnimationFrame(() => setShown(true)) })
      return () => { cancelAnimationFrame(outer); cancelAnimationFrame(inner) }
    }
    setShown(false)
    const timer = setTimeout(() => setMounted(false), duration)
    return () => clearTimeout(timer)
  }, [open, duration])

  return { mounted, shown }
}

// A little pop on elements that just became the spotlight target.
export function pulse(elements, reduced) {
  if (reduced) return
  elements.forEach(el => {
    if (!el.animate) return
    const lift = 1 + Math.min(0.06, 6 / Math.max(el.offsetWidth, 1))
    el.animate(
      [{ transform: 'scale(1)' }, { transform: `scale(${lift})` }, { transform: 'scale(1)' }],
      { duration: 420, easing: EASE },
    )
  })
}
